import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import arrowDown from "@/assets/icons/arrow-down.svg";

interface HeaderDropdownProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  icon?: any;
  disabled?: boolean;
}

export default function HeaderDropdown({
  options,
  value,
  onChange,
  icon,
  disabled = false,
}: HeaderDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rawValue = (value ?? "").toString().trim();
  const rawValueLower = rawValue.toLowerCase();
  const normalizedValue = rawValue.toUpperCase();

  const isValueInvalid =
    !rawValue || rawValueLower === "undefined" || rawValueLower === "null";

  const selectedOption = isValueInvalid
    ? options[0]
    : options.find((opt) => opt.value === rawValue) ||
      options.find((opt) => opt.value === normalizedValue) ||
      options[0];

  const selectedLabel = selectedOption?.label ?? options?.[0]?.label ?? "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-1 py-1 text-sm font-medium transition-colors ${
          disabled
            ? "text-[#D1D3D4] cursor-not-allowed"
            : "text-[#D1D3D4] hover:text-[#D1D3D4]/80"
        }`}
      >
        {icon && (
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.1364 7.31818C14.1364 9.12648 13.418 10.8607 12.1394 12.1394C10.8607 13.418 9.12648 14.1364 7.31818 14.1364M14.1364 7.31818C14.1364 5.50989 13.418 3.77566 12.1394 2.497C10.8607 1.21834 9.12648 0.5 7.31818 0.5M14.1364 7.31818C14.1364 9.05629 11.0839 10.465 7.31818 10.465C3.55245 10.465 0.5 9.05629 0.5 7.31818M14.1364 7.31818C14.1364 5.58007 11.0839 4.17133 7.31818 4.17133C3.55245 4.17133 0.5 5.58007 0.5 7.31818M7.31818 14.1364C5.50989 14.1364 3.77566 13.418 2.497 12.1394C1.21834 10.8607 0.5 9.12648 0.5 7.31818M7.31818 14.1364C9.05629 14.1364 10.465 11.0839 10.465 7.31818C10.465 3.55245 9.05629 0.5 7.31818 0.5M7.31818 14.1364C5.58007 14.1364 4.17133 11.0839 4.17133 7.31818C4.17133 3.55245 5.58007 0.5 7.31818 0.5M7.31818 0.5C5.50989 0.5 3.77566 1.21834 2.497 2.497C1.21834 3.77566 0.5 5.50989 0.5 7.31818"
              stroke="#D1D3D4"
            />
          </svg>
        )}
        <span>{selectedLabel}</span>
        {disabled && (
          <div className="animate-spin rounded-full h-3 w-3 border-b border-[#d9ac4f] ml-1"></div>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full right-0 mt-1 glass-effect rounded-lg shadow-lg border border-[#d9ac4f] py-1 min-w-[80px]">
          {options.map((option) => (
            <button
              key={option.value}
              className={`w-full text-right px-3 py-1.5 text-sm hover:bg-[#111111]/10 transition-colors ${
                option.value === value
                  ? "text-[#d9ac4f] font-medium"
                  : "text-[#D1D3D4]"
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
